import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import CountUp from "react-countup";
import { FaDownload, FaSync, FaTimes } from "react-icons/fa";
import {
  FiList, FiCheckCircle, FiXCircle, FiCalendar,
  FiTrendingUp, FiActivity, FiBriefcase, FiClipboard, FiSearch,
  FiAlertTriangle, FiShield, FiEye, FiBell
} from "react-icons/fi";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell
} from "recharts";
import "./EmployeeDashboard.css";

const API_BASE_URL = 'https://ingrainhirebackend.ingrainsystems.com/api';
const API_DOMAIN = 'https://ingrainhirebackend.ingrainsystems.com';

// --- Components from RecruitmentDashboard ---

const StatCard = ({ icon: Icon, label, value, color, isPercentage }) => {
  const themes = {
    indigo: "border-indigo-500",
    emerald: "border-emerald-500",
    amber: "border-amber-500",
    rose: "border-rose-500",
    cyan: "border-cyan-500",
  };
  const currentTheme = themes[color] || themes.indigo;

  return (
    <div className={`bg-white rounded-2xl border-l-4 ${currentTheme} shadow-lg shadow-gray-100 p-5 flex items-center justify-between hover:shadow-xl hover:shadow-${color}-100/50 transition-all duration-300 group`}>
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</p>
        <p className="text-3xl font-bold text-gray-900 mt-1 group-hover:text-${color}-600 transition-colors">
          {isPercentage ? (
            <>
              <CountUp end={value} duration={1.5} decimals={1} />%
            </>
          ) : (
            <CountUp end={value} duration={1.5} separator="," />
          )}
        </p>
      </div>
      <div className={`p-4 rounded-2xl bg-gradient-to-br from-${color}-50 to-${color}-100 group-hover:scale-110 transition-transform duration-300`}>
        <Icon className={`text-${color}-600`} size={24} />
      </div>
    </div>
  );
};

// Tooltip component
const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="px-3 py-2 text-xs bg-white border border-gray-100 rounded-lg shadow-xl">
      <p className="font-bold text-gray-800 mb-0.5 leading-none">{d.label}</p>
      <p className="leading-none text-gray-500">Count: {d.val}</p>
    </div>
  );
};

// Quality Card component for metrics
const QualityCard = ({ label, value, total, color }) => {
  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;

  const colorClasses = {
    rose: "bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200",
    amber: "bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200",
    emerald: "bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200",
    cyan: "bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200",
    indigo: "bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200",
    purple: "bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200",
  };

  return (
    <div className={`p-3 px-4 rounded-2xl border ${colorClasses[color] || colorClasses.indigo} shadow-md hover:shadow-lg transition-all duration-300 group`}>
      <div className="flex justify-between items-center mb-2">
        <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">{label}</span>
      </div>
      <div className="flex items-baseline justify-between">
        <span className="text-2xl font-black text-gray-900 group-hover:text-${color}-600 transition-colors">
          <CountUp end={value} duration={1.5} />
        </span>
        <span className="text-[11px] font-bold text-gray-700">{percentage}%</span>
      </div>
      <div className="w-full h-2 bg-white rounded-full mt-3 overflow-hidden shadow-inner">
        <div
          className={`h-full rounded-full shadow-sm ${color === 'rose' ? 'bg-gradient-to-r from-rose-500 to-rose-600' :
            color === 'amber' ? 'bg-gradient-to-r from-amber-500 to-amber-600' :
              color === 'emerald' ? 'bg-gradient-to-r from-emerald-500 to-emerald-600' :
                color === 'cyan' ? 'bg-gradient-to-r from-cyan-500 to-cyan-600' :
                  color === 'purple' ? 'bg-gradient-to-r from-purple-500 to-purple-600' :
                    'bg-gradient-to-r from-indigo-500 to-indigo-600'
            }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
const AllMedicalCertificate = () => {
  const [certificates, setCertificates]         = useState([]);
  const [filteredCertificates, setFiltered]     = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [currentPage, setCurrentPage]           = useState(1);
  const [itemsPerPage, setItemsPerPage]         = useState(10);
  const [searchTerm, setSearchTerm]             = useState("");
  const [statusFilter, setStatusFilter]         = useState("all");
  const [roleFilter, setRoleFilter]             = useState("");
  const [roleOpen, setRoleOpen]                 = useState(false);
  const [statusOpen, setStatusOpen]             = useState(false);
  const [roleSearch, setRoleSearch]             = useState("");
  const [roles, setRoles]                       = useState([]);
  const roleRef = useRef(null);
  const statusRef = useRef(null);

  const getClientId = () =>
    localStorage.getItem("clientId") || localStorage.getItem("clientCustomId") || "";

  useEffect(() => {
    fetchCertificates();
    fetchRoles();
    const handler = e => {
      if (roleRef.current && !roleRef.current.contains(e.target)) setRoleOpen(false);
      if (statusRef.current && !statusRef.current.contains(e.target)) setStatusOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const clientId = getClientId();
      const url = clientId
        ? `${API_BASE_URL}/medical-certificates/all/${clientId}`
        : `${API_BASE_URL}/medical-certificates/all`;
      const res = await axios.get(url);
      if (res.data.success) {
        const data = res.data.data || [];
        setCertificates(data);
        setFiltered(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const clientId = getClientId();
      const res = await axios.get(
        clientId ? `${API_BASE_URL}/jobs/all/${clientId}` : `${API_BASE_URL}/jobs/all`
      );
      if (res.data.success) {
        const jobs = res.data.jobPosts || res.data.data || [];
        const names = Array.from(
          new Set(jobs.map(j => j.role).filter(Boolean))
        );
        setRoles(names.map((name, i) => ({ id: i, name })));
      }
    } catch (err) {
      console.error("Error fetching roles:", err);
    }
  };

  const getStatusInfo = (expiryDate) => {
    if (!expiryDate) return { label: "Unknown", color: "#9CA3AF", text: "#6B7280", bg: "#f3f4f6", border: "#d1d5db" };
    const d = Math.ceil((new Date(expiryDate) - new Date()) / 86400000);
    if (d < 0)   return { label: "Expired",       color: "#EF4444", text: "#b91c1c", bg: "#fef2f2", border: "#fecaca" };
    if (d <= 30) return { label: "Expiring Soon", color: "#F87171", text: "#dc2626", bg: "#fff1f2", border: "#fecdd3" };
    if (d <= 60) return { label: "Due in 2 Mo.",  color: "#FB923C", text: "#c2410c", bg: "#fff7ed", border: "#fed7aa" };
    return             { label: "Active",          color: "#10B981", text: "#047857", bg: "#ecfdf5", border: "#a7f3d0" };
  };

  const getStats = () => {
    const m = { Expired: 0, "Expiring Soon": 0, "Due in 2 Mo.": 0, Active: 0 };
    certificates.forEach(c => {
      const l = getStatusInfo(c.expiryDate).label;
      if (m[l] !== undefined) m[l]++;
    });
    return [
      { label: "EXPIRED",  val: m.Expired,           color: "#EF4444" },
      { label: "NEXT 30D", val: m["Expiring Soon"],  color: "#F87171" },
      { label: "NEXT 60D", val: m["Due in 2 Mo."],   color: "#FB923C" },
      { label: "ACTIVE",   val: m.Active,             color: "#10B981" },
    ];
  };

  // Apply filters whenever certificates or filter values change
  useEffect(() => {
    let filtered = [...certificates];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(c => {
        const name = (c.employeeName || c.candidateName || "").toLowerCase();
        const id = (c.employeeId || c.candidateId || "").toLowerCase();
        return name.includes(term) || id.includes(term);
      });
    }

    // Role filter
    if (roleFilter) {
      const roleFilterLower = roleFilter.toLowerCase().trim();
      filtered = filtered.filter(c => {
        const certRole = (c.jobId?.role || c.role || "").toLowerCase().trim();
        return certRole === roleFilterLower;
      });
    }

    // Status filter
    if (statusFilter !== "all") {
      const statusMap = {
        expired: "Expired",
        next_month: "Expiring Soon",
        "2_months": "Due in 2 Mo.",
        active: "Active"
      };
      const targetStatus = statusMap[statusFilter];
      filtered = filtered.filter(c => {
        const info = getStatusInfo(c.expiryDate);
        return info.label === targetStatus;
      });
    }

    setFiltered(filtered);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, roleFilter, certificates]);

  const handleSendReminder = async cert => {
    try {
      const recipientId = cert.employeeId || cert.candidateId;
      if (!recipientId) return alert("Recipient ID not found");
      await axios.post(`${API_BASE_URL}/medical-certificates/remind`, {
        recipientId,
        message: `RE-UPLOAD REQUIRED: Your medical certificate expires on ${new Date(cert.expiryDate).toLocaleDateString()}. Please upload a new one.`,
      });
      alert(`Reminder sent to ${cert.employeeName || cert.candidateName}`);
    } catch {
      alert("Failed to send reminder. Please try again.");
    }
  };

  const downloadCSV = () => {
    if (!filteredCertificates.length) return alert("No data to export!");
    const rows = [
      ["ID", "Name", "Reg. Date", "Exp. Date", "Status"].join(","),
      ...filteredCertificates.map(c => {
        const info = getStatusInfo(c.expiryDate);
        return [
          `"${c.employeeId || c.candidateId || "N/A"}"`,
          `"${c.employeeName || c.candidateName || "N/A"}"`,
          `"${new Date(c.registrationDate).toLocaleDateString()}"`,
          `"${new Date(c.expiryDate).toLocaleDateString()}"`,
          `"${info.label}"`,
        ].join(",");
      }),
    ];
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([rows.join("\n")], { type: "text/csv" }));
    a.download = `medical_certificates_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const handleReset = () => {
    setSearchTerm(""); 
    setStatusFilter("all"); 
    setRoleFilter(""); 
    setRoleSearch(""); 
    setStatusOpen(false);
    setRoleOpen(false);
    fetchCertificates();
  };

  const chartData     = getStats();
  const total         = certificates.length;
  const criticalCount = chartData[0].val + chartData[1].val;
  const idxLast       = currentPage * itemsPerPage;
  const currentItems  = filteredCertificates.slice(idxLast - itemsPerPage, idxLast);
  const totalPages    = Math.ceil(filteredCertificates.length / itemsPerPage);
  const hasFilter     = searchTerm || statusFilter !== "all" || roleFilter;
  const complianceRate = total > 0 ? ((chartData[3].val / total) * 100).toFixed(1) : 0;

  const avatarPalette = [
    "linear-gradient(135deg,#2563eb,#7c3aed)",
    "linear-gradient(135deg,#0891b2,#2563eb)",
    "linear-gradient(135deg,#059669,#0891b2)",
    "linear-gradient(135deg,#d97706,#dc2626)",
    "linear-gradient(135deg,#7c3aed,#db2777)",
  ];

  /* ── Loading ── */
  if (loading)
    return (
      <div className="admin-dash">
        <div className="admin-dash__loading">
          <div className="admin-dash__spinner" />
          <p className="admin-dash__loading-text">Loading certificates…</p>
        </div>
      </div>
    );

  return (
    <div className="admin-dash">
      <main className="">

        {/* Header */}
        <div className="admin-dash__header">
          <div>
            <h1 className="admin-dash__greeting">
              Medical <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Certificates</span>
            </h1>
            <p className="admin-dash__subtitle">
              Compliance tracking · <strong>{total}</strong> total records · Compliance rate: <strong>{complianceRate}%</strong>
            </p>
          </div>
          <div className="admin-dash__date-pill bg-gradient-to-r from-indigo-50 via-blue-50 to-purple-50 border-indigo-200 shadow-lg shadow-indigo-100">
            <FiCalendar className="text-indigo-600" />
            <span className="text-indigo-700 font-semibold">
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>

        {/* KPI Stats */}
        <div className="admin-dash__stats">
          <div className="admin-dash__stat">
            <div className="admin-dash__stat-top">
              <span className="admin-dash__stat-label">Critical Risk</span>
              <div className="admin-dash__stat-icon admin-dash__stat-icon--rose">
                <FiAlertTriangle />
              </div>
            </div>
            <div className="admin-dash__stat-value"><CountUp end={criticalCount} duration={2} separator="," /></div>
            <div className="admin-dash__stat-meta">immediate action required</div>
          </div>
          <div className="admin-dash__stat">
            <div className="admin-dash__stat-top">
              <span className="admin-dash__stat-label">Due in 60 Days</span>
              <div className="admin-dash__stat-icon admin-dash__stat-icon--amber">
                <FiCalendar />
              </div>
            </div>
            <div className="admin-dash__stat-value"><CountUp end={chartData[2].val} duration={2} separator="," /></div>
            <div className="admin-dash__stat-meta">expiring soon</div>
          </div>
          <div className="admin-dash__stat">
            <div className="admin-dash__stat-top">
              <span className="admin-dash__stat-label">Active</span>
              <div className="admin-dash__stat-icon admin-dash__stat-icon--emerald">
                <FiCheckCircle />
              </div>
            </div>
            <div className="admin-dash__stat-value"><CountUp end={chartData[3].val} duration={2} separator="," /></div>
            <div className="admin-dash__stat-meta">valid certificates</div>
          </div>
          <div className="admin-dash__stat">
            <div className="admin-dash__stat-top">
              <span className="admin-dash__stat-label">Monitoring</span>
              <div className="admin-dash__stat-icon admin-dash__stat-icon--indigo">
                <FiActivity />
              </div>
            </div>
            <div className="admin-dash__stat-value"><CountUp end={total} duration={2} separator="," /></div>
            <div className="admin-dash__stat-meta">under surveillance</div>
          </div>
          <div className="admin-dash__stat">
            <div className="admin-dash__stat-top">
              <span className="admin-dash__stat-label">Total Records</span>
              <div className="admin-dash__stat-icon admin-dash__stat-icon--cyan">
                <FiList />
              </div>
            </div>
            <div className="admin-dash__stat-value"><CountUp end={total} duration={2} separator="," /></div>
            <div className="admin-dash__stat-meta">all certificates</div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="admin-dash__charts-grid">
          {/* Bar Chart */}
          <div className="admin-dash__card admin-dash__chart-wrap">
            <div className="admin-dash__card-header">
              <div>
                <h3 className="admin-dash__card-title">Compliance Health</h3>
                <p className="admin-dash__card-desc">Certificate risk distribution by status</p>
              </div>
              <FiTrendingUp className="text-xl text-gray-400" />
            </div>
            <div className="admin-dash__card-body flex-1">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 10, fontWeight: 600 }} interval={0} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#98a2b3', fontSize: 11 }} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: '#f8fafc', radius: 6 }} />
                  <Bar dataKey="val" radius={[7, 7, 0, 0]} barSize={36}>
                    {chartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Risk Breakdown */}
          <div className="admin-dash__card admin-dash__chart-wrap">
            <div className="admin-dash__card-header">
              <div>
                <h3 className="admin-dash__card-title">Risk Breakdown</h3>
                <p className="admin-dash__card-desc">Percentage analysis of compliance states</p>
              </div>
              <FiActivity className="text-xl text-rose-400" />
            </div>
            <div className="admin-dash__card-body">
              <div className="grid grid-cols-2 gap-3 mb-4">
                <QualityCard label="Critical (Expired+30d)" value={criticalCount} total={total} color="rose" />
                <QualityCard label="Due in 2 Months" value={chartData[2].val} total={total} color="amber" />
                <QualityCard label="Active" value={chartData[3].val} total={total} color="emerald" />
                <QualityCard label="Overall Monitored" value={total} total={total} color="indigo" />
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-500">Compliance Rate</span>
                  <span className="font-bold text-emerald-600">{complianceRate}%</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full" style={{ width: `${complianceRate}%` }} />
                </div>
                <div className="flex justify-between text-xs mt-2">
                  <span className="text-gray-500">Immediate Action Required</span>
                  <span className="font-bold text-rose-600">{criticalCount} records</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="emp-dash__card mb-6 shadow-lg shadow-gray-100">
          <div className="emp-dash__card-header bg-gradient-to-r from-slate-50 to-blue-50">
            <div>
              <h3 className="emp-dash__card-title flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-md">
                  <FiSearch size={14} />
                </div>
                Filter Records
              </h3>
              <p className="emp-dash__card-desc">Search by name, ID, role, or department</p>
            </div>
          </div>
          <div className="emp-dash__card-body bg-gradient-to-br from-white via-gray-50 to-blue-50/20">
            <div className="flex flex-wrap items-center gap-3">
              {/* Search Bar */}
              <div className="relative flex-1 min-w-[200px]">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  placeholder="Search name, ID..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white shadow-sm hover:shadow-md transition-shadow"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1 transition-colors"
                  >
                    <FaTimes size={12} />
                  </button>
                )}
              </div>

              {/* Role Filter Button */}
              <div className="relative" ref={roleRef}>
                <button
                  onClick={() => setRoleOpen(o => !o)}
                  className={`h-10 px-4 py-2.5 text-sm font-medium rounded-xl transition flex items-center gap-2 shadow-sm hover:shadow-md ${
                    roleFilter 
                      ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-100' 
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <FiBriefcase className={roleFilter ? "text-blue-100" : "text-gray-400"} size={14} /> Role {roleFilter && `: ${roleFilter}`}
                  <span className={`text-gray-400 transition-transform ${roleOpen ? 'rotate-180' : ''}`}>▾</span>
                </button>
                {roleOpen && (
                  <div className="absolute z-50 mt-2 w-56 bg-white border border-gray-200 rounded-2xl shadow-2xl max-h-72 overflow-y-auto">
                    <div className="p-3 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-blue-50 sticky top-0">
                      <div className="relative">
                        <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                        <input
                          type="text"
                          className="w-full py-2.5 pl-10 pr-4 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-sm"
                          placeholder="Search roles..."
                          value={roleSearch}
                          onChange={e => setRoleSearch(e.target.value)}
                          onClick={e => e.stopPropagation()}
                          autoFocus
                        />
                      </div>
                    </div>
                    <div
                      onClick={() => { setRoleFilter(""); setRoleOpen(false); setRoleSearch(""); }}
                      className={`px-4 py-3 text-sm hover:bg-blue-50 cursor-pointer border-b border-gray-100 font-medium transition-colors ${
                        !roleFilter ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      All Roles
                    </div>
                    {roles
                      .filter(r => r.name.toLowerCase().includes(roleSearch.toLowerCase()))
                      .map((role) => (
                        <div
                          key={role.id}
                          onClick={() => { setRoleFilter(role.name); setRoleOpen(false); setRoleSearch(""); }}
                          className={`px-4 py-3 text-sm hover:bg-blue-50 cursor-pointer transition-colors ${
                            roleFilter === role.name ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'
                          }`}
                        >
                          {role.name}
                        </div>
                      ))}
                    {roles.filter(r => r.name.toLowerCase().includes(roleSearch.toLowerCase())).length === 0 && (
                      <div className="px-4 py-6 text-sm text-gray-400 text-center">No roles found</div>
                    )}
                  </div>
                )}
              </div>

              {/* Status Filter Button */}
              <div className="relative" ref={statusRef}>
                <button
                  onClick={() => setStatusOpen(o => !o)}
                  className={`h-10 px-4 py-2.5 text-sm font-medium rounded-xl transition flex items-center gap-2 shadow-sm hover:shadow-md ${
                    statusFilter !== "all"
                      ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-100' 
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <FiCheckCircle className={statusFilter !== "all" ? "text-blue-100" : "text-gray-400"} size={14} /> Status {statusFilter !== "all" && `: ${statusFilter}`}
                  <span className={`text-gray-400 transition-transform ${statusOpen ? 'rotate-180' : ''}`}>▾</span>
                </button>
                {statusOpen && (
                  <div className="absolute z-50 mt-2 w-56 bg-white border border-gray-200 rounded-2xl shadow-2xl max-h-72 overflow-y-auto">
                    <div
                      onClick={() => { setStatusFilter("all"); setStatusOpen(false); }}
                      className={`px-4 py-3 text-sm hover:bg-blue-50 cursor-pointer border-b border-gray-100 font-medium transition-colors ${
                        statusFilter === "all" ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      All Statuses
                    </div>
                    <div
                      onClick={() => { setStatusFilter("expired"); setStatusOpen(false); }}
                      className={`px-4 py-3 text-sm hover:bg-blue-50 cursor-pointer transition-colors ${
                        statusFilter === "expired" ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'
                      }`}
                    >
                      Expired
                    </div>
                    <div
                      onClick={() => { setStatusFilter("next_month"); setStatusOpen(false); }}
                      className={`px-4 py-3 text-sm hover:bg-blue-50 cursor-pointer transition-colors ${
                        statusFilter === "next_month" ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'
                      }`}
                    >
                      Expiring Soon
                    </div>
                    <div
                      onClick={() => { setStatusFilter("2_months"); setStatusOpen(false); }}
                      className={`px-4 py-3 text-sm hover:bg-blue-50 cursor-pointer transition-colors ${
                        statusFilter === "2_months" ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'
                      }`}
                    >
                      Due in 2 Mo.
                    </div>
                    <div
                      onClick={() => { setStatusFilter("active"); setStatusOpen(false); }}
                      className={`px-4 py-3 text-sm hover:bg-blue-50 cursor-pointer transition-colors ${
                        statusFilter === "active" ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'
                      }`}
                    >
                      Active
                    </div>
                  </div>
                )}
              </div>

              {/* Clear Filters Button */}
              {(searchTerm || statusFilter !== "all" || roleFilter) && (
                <button
                  onClick={handleReset}
                  className="h-10 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm hover:shadow-md"
                >
                  <FaTimes size={14} /> Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="admin-dash__card">
          <div className="admin-dash__card-header flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <h3 className="admin-dash__card-title">Certificate Records</h3>
              <p className="admin-dash__card-desc">
                Showing <strong>{filteredCertificates.length}</strong> of <strong>{total}</strong> records
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={downloadCSV}
                className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 text-emerald-600 border border-emerald-200 rounded-xl text-xs font-bold hover:bg-emerald-600 hover:text-white transition-all shadow-sm hover:shadow-md"
              >
                <FaDownload size={12} /> Export
              </button>
              <span className="text-xs text-gray-500 font-medium">Rows:</span>
              <select
                value={itemsPerPage}
                onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="px-3 py-2 border border-gray-200 rounded-xl text-xs text-gray-800 bg-white outline-none cursor-pointer shadow-sm hover:shadow-md transition-shadow"
              >
                {[5, 10, 20, 50].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] border-collapse">
              <thead>
                <tr className="bg-gradient-to-r from-slate-50 to-blue-50">
                  {["Candidate", "Reg. Date", "Exp. Date", "Status", "Actions"].map((h, i) => (
                    <th
                      key={h}
                      className={`px-4 py-4 text-xs font-bold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200 whitespace-nowrap ${i === 0 ? 'text-left pl-5' : 'text-center'}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((c, rowIdx) => {
                    const info = getStatusInfo(c.expiryDate);
                    const name = c.employeeName || c.candidateName || "N/A";
                    const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                    return (
                      <tr
                        key={c._id}
                        className="border-b border-gray-100 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-indigo-50/50 transition-all duration-200 group animate-in fade-in slide-in-from-bottom-2"
                        style={{ animationDelay: `${rowIdx * 0.05}s` }}
                      >
                        {/* Candidate */}
                        <td className="px-4 py-4 pl-5">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-200" style={{ background: avatarPalette[rowIdx % avatarPalette.length] }}>
                              {initials}
                            </div>
                            <div>
                              <div className="font-bold text-sm text-gray-900 whitespace-nowrap group-hover:text-blue-600 transition-colors">{name}</div>
                              <div className="text-[11px] text-gray-500 font-medium mt-0.5">{c.jobId?.role || c.role || "—"}</div>
                            </div>
                          </div>
                        </td>

                        {/* ID */}
                        {/* <td className="text-center px-4 py-3">
                          <span className="text-[11px] font-bold text-gray-600 bg-gray-100 px-2.5 py-1 rounded-md tracking-wide">
                            {c.employeeId || c.candidateId || "N/A"}
                          </span>
                        </td> */}

                        {/* Reg Date */}
                        <td className="text-center px-4 py-4 text-gray-600 text-sm font-medium">
                          {c.registrationDate ? new Date(c.registrationDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "N/A"}
                        </td>

                        {/* Exp Date */}
                        <td className="text-center px-4 py-4">
                          <span className="text-sm font-bold" style={{ color: info.text }}>
                            {c.expiryDate ? new Date(c.expiryDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "N/A"}
                          </span>
                        </td>

                        {/* Status */}
                        <td className="text-center px-4 py-4">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider whitespace-nowrap shadow-sm" style={{ background: info.bg, color: info.text, border: `1.5px solid ${info.border}` }}>
                            <span className="w-2 h-2 rounded-full flex-shrink-0 shadow-sm" style={{ background: info.color }} />
                            {info.label}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="text-center px-4 py-4">
                          <div className="flex items-center justify-center gap-2">
                            {c.documentUrl && (
                              <a
                                href={`${API_DOMAIN}${c.documentUrl}`}
                                target="_blank" rel="noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-2 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-xl text-xs font-bold no-nowrap whitespace-nowrap hover:bg-indigo-600 hover:text-white transition-all duration-200 hover:scale-105 shadow-sm"
                              >
                                <FiEye size={12} /> View
                              </a>
                            )}
                            {info.label !== "Active" && info.label !== "Unknown" && (
                              <button
                                onClick={() => handleSendReminder(c)}
                                className="inline-flex items-center gap-1.5 px-3 py-2 bg-rose-50 text-rose-600 border border-rose-200 rounded-xl text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-rose-600 hover:text-white hover:border-rose-600 transition-all duration-200 hover:scale-105 shadow-sm"
                              >
                                <FiBell size={12} /> Remind
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center py-20 text-gray-500">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-20 h-20 bg-gradient-to-br from-gray-50 to-blue-50 rounded-3xl flex items-center justify-center shadow-lg shadow-gray-100">
                          <FiSearch size={32} className="text-gray-300" />
                        </div>
                        <div className="font-bold text-lg text-gray-800">No records found</div>
                        <div className="text-sm text-gray-500 mb-4">Try adjusting your search or filter criteria</div>
                        <button
                          onClick={handleReset}
                          className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl text-sm font-semibold shadow-lg shadow-blue-200 hover:shadow-xl hover:scale-105 transition-all duration-200"
                        >
                          Clear Filters
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col items-center justify-between px-6 py-4 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50/30 sm:flex-row">
              <span className="text-sm text-gray-600">
                Showing <b className="text-gray-900">{idxLast - itemsPerPage + 1}</b>–<b className="text-gray-900">{Math.min(idxLast, filteredCertificates.length)}</b> of <b className="text-gray-900">{filteredCertificates.length}</b>
              </span>
              <div className="flex gap-2 mt-4 sm:mt-0">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 bg-white disabled:text-gray-400 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-gray-300 hover:shadow-md transition-all shadow-sm"
                >
                  ← Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                  .reduce((acc, p, i, arr) => {
                    if (i > 0 && p - arr[i - 1] > 1) acc.push("…");
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) => (
                    <button
                      key={i}
                      onClick={() => typeof p === "number" && setCurrentPage(p)}
                      disabled={p === "…"}
                      className={`w-10 h-10 border rounded-xl text-sm font-bold transition-all shadow-sm ${p === currentPage ? 'border-indigo-500 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-lg shadow-indigo-200 hover:shadow-xl hover:scale-105' : 'border-gray-200 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 hover:shadow-md'} disabled:cursor-default`}
                    >
                      {p}
                    </button>
                  ))}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 bg-white disabled:text-gray-400 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-gray-300 hover:shadow-md transition-all shadow-sm"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>

      </main>
    </div>
  );
};

export default AllMedicalCertificate;